import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../../config/firebase';
import { collection, addDoc, onSnapshot, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { recommendedSwissRounds } from '../../utils/swissPairing';
import Icon from '../Shared/Icons';

const EMPTY_FORM = { name: '', description: '', maxParticipants: 500, clockMinutes: 15, prizes: '', rules: '' };

export default function TournamentLobby() {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const isOrganizer = userRole === 'admin' || userRole === 'staff';

  const [tournaments, setTournaments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'tournaments'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setTournaments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const ref = await addDoc(collection(db, 'tournaments'), {
        name: form.name,
        description: form.description,
        maxParticipants: Number(form.maxParticipants) || 500,
        clockSeconds: (Number(form.clockMinutes) || 15) * 60,
        prizes: form.prizes,
        rules: form.rules || null,
        status: 'registration',
        currentRound: 0,
        currentPhase: null,
        winnerId: null,
        winnerName: null,
        createdAt: serverTimestamp(),
      });
      setShowForm(false);
      setForm(EMPTY_FORM);
      navigate(`/chess/tournaments/${ref.id}`);
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  }

  const statusLabel = (t) => {
    if (t.status === 'registration') return { text: 'Registration Open', color: '#57A97C' };
    if (t.status === 'swiss') return { text: `Swiss — Round ${t.currentRound}`, color: '#34D399' };
    if (t.status === 'knockout') return { text: `Knockout — Round ${t.currentRound}`, color: '#EF5350' };
    return { text: 'Finished', color: '#9CA3AF' };
  };

  return (
    <div style={styles.page}>
      <div style={styles.bar}>
        <button style={styles.backIconBtn} onClick={() => navigate('/chess')}>
          <Icon name="door" size={19} color="#ECEFEA" strokeWidth={2.4} />
        </button>
        <div style={styles.barTitle}>Mashindano</div>
        {isOrganizer ? (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Link to="/chess/admin" style={{ ...styles.createBtn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>Admin</Link>
            <button style={styles.createBtn} onClick={() => setShowForm(!showForm)}>
              {showForm ? 'Funga' : '+ Unda'}
            </button>
          </div>
        ) : <div style={{ width: '38px' }} />}
      </div>

      {showForm && (
        <form onSubmit={handleCreate} style={styles.formCard}>
          <input style={styles.input} placeholder="Tournament name" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })} required />
          <textarea style={{ ...styles.input, height: '70px' }} placeholder="Description"
            value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          <div style={styles.formRow}>
            <div style={{ flex: 1 }}>
              <label style={styles.label}>Max Participants</label>
              <input style={styles.input} type="number" min="4" max="500" value={form.maxParticipants}
                onChange={e => setForm({ ...form, maxParticipants: e.target.value })} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={styles.label}>Clock (minutes/side)</label>
              <input style={styles.input} type="number" min="3" max="60" value={form.clockMinutes}
                onChange={e => setForm({ ...form, clockMinutes: e.target.value })} />
            </div>
          </div>
          <textarea style={{ ...styles.input, height: '50px' }} placeholder="Prizes (e.g. Trophy + Iyanna Foundation Certificate + Chess Set)"
            value={form.prizes} onChange={e => setForm({ ...form, prizes: e.target.value })} />
          <textarea style={{ ...styles.input, height: '90px' }} placeholder="Rules za mashindano (acha wazi kutumia rules za kawaida za Iyanna)"
            value={form.rules} onChange={e => setForm({ ...form, rules: e.target.value })} />
          <div style={styles.hintText}>
            Suggested Swiss rounds for {form.maxParticipants || 0} players: {recommendedSwissRounds(Number(form.maxParticipants) || 2)}
          </div>
          <button type="submit" style={styles.submitBtn} disabled={saving}>
            {saving ? 'Creating...' : 'Create Tournament'}
          </button>
        </form>
      )}

      {tournaments.length === 0 ? (
        <div style={styles.empty}>Hakuna mashindano bado. {isOrganizer ? 'Anza moja hapo juu!' : 'Rudi baadaye.'}</div>
      ) : (
        <div style={styles.list}>
          {tournaments.map(t => {
            const label = statusLabel(t);
            return (
              <div key={t.id} style={{ ...styles.card, ...(t.status === 'registration' ? styles.cardOpen : {}) }} onClick={() => navigate(`/chess/tournaments/${t.id}`)}>
                <div style={styles.cardTop}>
                  <div style={styles.cardName}>{t.name}</div>
                  <span style={{ ...styles.statusTag, color: label.color, borderColor: label.color }}>
                    <span style={{ ...styles.statusDot, background: label.color }} />
                    {label.text}
                  </span>
                </div>
                {t.description && <div style={styles.cardDesc}>{t.description}</div>}
                <div style={styles.cardChips}>
                  <span style={styles.cardChip}><Icon name="user" size={12} color="rgba(236,239,234,0.6)" /> {t.maxParticipants}</span>
                  <span style={styles.cardChip}><Icon name="clock" size={12} color="rgba(236,239,234,0.6)" /> {Math.round((t.clockSeconds || 900) / 60)} min</span>
                  {t.prizes && <span style={{ ...styles.cardChip, color: '#34D399', borderColor: 'rgba(52,211,153,0.4)' }}><Icon name="gift" size={12} color="#34D399" /> {t.prizes}</span>}
                </div>
                {t.status === 'finished' && t.winnerName ? (
                  <div style={styles.winnerTag}><Icon name="crown" size={13} color="#34D399" /> Bingwa: {t.winnerName}</div>
                ) : t.status === 'registration' ? (
                  <div style={styles.joinRow}>Jiunge sasa →</div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  bar: { maxWidth: '760px', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' },
  backIconBtn: { width: '38px', height: '38px', borderRadius: '11px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.09)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  barTitle: { color: '#ECEFEA', fontFamily: "'Fraunces', Georgia, serif", fontSize: '19px', fontWeight: '800' },
  cardOpen: { border: '1.5px solid rgba(52,211,153,0.45)', boxShadow: '0 12px 30px rgba(52,211,153,0.08)' },
  statusDot: { width: '7px', height: '7px', borderRadius: '50%', display: 'inline-block' },
  cardChips: { display: 'flex', gap: '7px', flexWrap: 'wrap', marginTop: '10px' },
  cardChip: { display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(236,239,234,0.7)', fontSize: '10.5px', fontWeight: '700', padding: '5px 11px', borderRadius: '99px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  joinRow: { marginTop: '11px', color: '#34D399', fontSize: '12.5px', fontWeight: '800' },

  page: { minHeight: '100vh', background: 'radial-gradient(circle at 30% 0%, #262D2A 0%, #1B201E 48%, #121614 100%)', padding: '20px 16px 60px' },
  header: { maxWidth: '700px', margin: '0 auto 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px' },
  brandRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  brandLogo: { width: '46px', height: '46px', borderRadius: '50%', background: '#121614', padding: '0px', border: '2px solid rgba(255,255,255,0.15)', overflow: 'hidden'},
  brandName: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '19px', color: '#fff', fontWeight: '700' },
  brandSub: { fontSize: '11px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none' },
  createBtn: { background: 'linear-gradient(135deg, #10B981, #34D399)', border: 'none', color: '#fff', padding: '10px 18px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  formCard: { maxWidth: '700px', margin: '0 auto 20px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '10px' },
  input: { width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.07)', border: '1.5px solid rgba(255,255,255,0.15)', borderRadius: '10px', padding: '11px 14px', color: '#fff', fontSize: '13.5px', outline: 'none', fontFamily: 'inherit' },
  formRow: { display: 'flex', gap: '10px' },
  label: { color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginBottom: '4px', display: 'block' },
  hintText: { color: 'rgba(255,255,255,0.4)', fontSize: '11.5px' },
  submitBtn: { padding: '13px', background: 'linear-gradient(135deg, #14452F, #1F6B45)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  empty: { maxWidth: '700px', margin: '0 auto', color: 'rgba(255,255,255,0.4)', fontSize: '13px', textAlign: 'center', padding: '30px 0' },
  list: { maxWidth: '700px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '12px' },
  card: { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px', padding: '18px', cursor: 'pointer' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' },
  cardName: { color: '#fff', fontFamily: "'Fraunces', Georgia, serif", fontSize: '16px', fontWeight: '700' },
  statusTag: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '700', border: '1px solid', borderRadius: '20px', padding: '4px 11px' },
  cardDesc: { color: 'rgba(255,255,255,0.55)', fontSize: '12.5px', marginBottom: '8px' },
  cardMeta: { color: 'rgba(255,255,255,0.4)', fontSize: '11.5px' },
  winnerTag: { marginTop: '8px', color: '#4ADE80', fontSize: '12.5px', fontWeight: '700' },
};
